import {
  Component,
  computed,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { twMerge } from '../../utils';
import { A11yService } from '../../services/a11y/a11y.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'da-skip-nav',
  standalone: true,
  providers: [A11yService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './skip-nav.component.html',
})
/**
 * This component acts as a Bypass Block for WCAG compliance.
 * This allows users to skip repetitive content and navigate directly to the main content of the page.
 */
export class SkipNavComponent {
  /**
   * The ID of the element to which the focus will be moved.
   * The first tabbable child element of containerName will receive focus if the containerName element is not tabbable
   */
  public containerName = input.required<string>();
  /**
   * The text that should display on the skip nav button
   */
  public linkText = input.required<string>();
  /**
   * The unique ID of the button, based on the containerName input
   */
  public buttonId = computed<string>(
    () => `skipnav-da-${this.containerName()}`
  );
  /**
   * Allows us to merge and override Tailwind CSS class names
   */
  public twMerge = twMerge;
  /**
   * The Tailwind class names for the skip nav button.
   * The default margin-left value is set to -1000px to hide the button off-screen until focused.
   * The focus state will override the margin-left value to show the button
   */
  public buttonClasses: string = twMerge(
    'tw-ml-[-1000px] tw-btn-medium tw-btn-secondary-outline focus:tw-ml-1 focus:tw-mt-1 rtl:focus:tw-mr-1 rtl:tw-mr-[-1000px]'
  );

  constructor(private a11yService: A11yService) {}

  /**
   * When the 'Enter' or 'Space' keys are pressed while focused on the Skip Nav button,
   * OR when the Skip Nav button is clicked,
   * The keyboard focus is moved to the first tabbable element within the containerName element
   * @param event The keyboard's 'Enter' or 'Space' event OR a mouse click (pointer) event
   */
  public moveFocus(event: Event): void {
    event?.preventDefault();
    this.a11yService.moveFocusToElement(this.containerName());
  }
}
